import { useEffect, useState, useCallback } from 'react';
import { PanelRight, Search, SquarePlus, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { fetchChatHeaders } from '../../services/chatService'; // or from chatHeaderService
import { getUserIdFromToken } from '../../utils/authUtils';
import { useSidebarContext } from '../../context/SidebasContext';
import { useChatHeadersContext } from '../../context/ChatHeadersContext';
import { useModuleContext } from '../../context/ModuleContext';

export default function Sidebar({ isOpen, toggleSidebar }) {
  const navigate = useNavigate();
  const { refreshTrigger } = useSidebarContext();
  const { getCurrentApplicationId, selectedModule } = useModuleContext();
  const {
    chatHeaders,
    setChatHeaders,
    appendChatHeaders,
    hasMore,
    setHasMore,
    currentPage,
    setCurrentPage,
    isLoading,
    setIsLoading
  } = useChatHeadersContext();
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const loadChatHeaders = async (page = 1, append = false, pageSize = 10) => {
    const applicationId = getCurrentApplicationId();
    const userId = getUserIdFromToken();

    if (!applicationId || !userId) return;

    setIsLoading(true);
    try {
      const response = await fetchChatHeaders(applicationId, userId, page, pageSize);
      const headers = response.data; // Only data array is returned
      if (append) {
        appendChatHeaders(headers);
        setCurrentPage(page); // Update current page when appending
      } else {
        setChatHeaders(headers);
        setCurrentPage(1); // Reset to first page when loading fresh
      }

      // If we received fewer headers than the pageSize, we've reached the end
      setHasMore(headers.length === pageSize);
    } catch (error) {
      console.error('Error fetching chat headers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreChatHeaders = async () => {
    if (isLoading || !hasMore) return;

    const nextPage = currentPage + 1;
    await loadChatHeaders(nextPage, true);
  };

  // Function to parse timestamp correctly (handles various formats)
  const parseTimestamp = (timestamp) => {
    // Handle various timestamp formats that might come from the API
    
    // Case 1: Already has timezone info (Z, +XX:XX, -XX:XX)
    if (timestamp.includes('Z') || /[+-]\d{2}:\d{2}$/.test(timestamp)) {
      return new Date(timestamp);
    }
    
    // Case 2: ISO format without timezone - treat as UTC (standard behavior)
    // This handles formats like "2025-06-09T15:45:45.651000" or "2024-01-15T12:30:45"
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(timestamp)) {
      return new Date(timestamp + 'Z');
    }
    
    // Case 3: Other date formats - try parsing as-is first
    const directParse = new Date(timestamp);
    if (!isNaN(directParse.getTime())) {
      return directParse;
    }
    
    // Case 4: Fallback - assume it's a Unix timestamp if it's all numbers
    if (/^\d+$/.test(timestamp)) {
      // Handle both seconds and milliseconds timestamps
      const num = parseInt(timestamp);
      return new Date(num > 1e10 ? num : num * 1000);
    }
    
    // Final fallback
    return new Date(timestamp);
  };

  // Function to format timestamp to local time
  const formatTimestamp = (timestamp, isToday = false, isYesterday = false) => {
    const date = parseTimestamp(timestamp);
    
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    if (isToday || isYesterday) {
      // For today and yesterday, show time only in local timezone
      return date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (getDateCategory(timestamp) === 'last7days') {
      // For last 7 days, show month and day in local timezone
      return date.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric' 
      });
    } else {
      // For older dates, show month, day, and year in local timezone
      return date.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    }
  };

  // Function to determine date category (using local time for comparison)
  const getDateCategory = (timestamp) => {
    const chatDate = parseTimestamp(timestamp);
    
    if (isNaN(chatDate.getTime())) {
      return 'older'; // Default for invalid dates
    }
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const chatDateOnly = new Date(chatDate.getFullYear(), chatDate.getMonth(), chatDate.getDate());

    if (chatDateOnly.getTime() === today.getTime()) {
      return 'today';
    } else if (chatDateOnly.getTime() === yesterday.getTime()) {
      return 'yesterday';
    } else if (chatDateOnly >= sevenDaysAgo) {
      return 'last7days';
    } else {
      return 'older';
    }
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter chat headers based on search query
  const filteredChatHeaders = useCallback(() => {
    if (!debouncedQuery.trim()) return chatHeaders;
    
    const query = debouncedQuery.toLowerCase();
    return chatHeaders.filter(chat => 
      chat.header.toLowerCase().includes(query)
    );
  }, [chatHeaders, debouncedQuery]);

  // Update the groupChatsByDate function to use filtered headers
  const groupChatsByDate = () => {
    const groups = {
      today: [],
      yesterday: [],
      last7days: [],
      older: [],
    };

    filteredChatHeaders().forEach(chat => {
      const category = getDateCategory(chat.timestamp);
      groups[category].push(chat);
    });

    // Sort each group by timestamp (newest first)
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
    });

    return groups;
  };

  const chatGroups = groupChatsByDate();

  // // Debug: Log when chatHeaders changes
  // useEffect(() => {
  //   console.log('chatHeaders updated:', chatHeaders.length, 'items');
  // }, [chatHeaders]);

  useEffect(() => {
    if (isOpen) loadChatHeaders();
  }, [isOpen]);

  // Refresh chat headers when refresh trigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      // Reset pagination state and load fresh data
      setCurrentPage(1);
      setHasMore(true);
      loadChatHeaders(1, false);
    }
  }, [refreshTrigger]);

  // Fetch chat headers when module changes
  useEffect(() => {
    if (isOpen) {
      // Reset pagination state and load fresh data for the new module
      setCurrentPage(1);
      setHasMore(true);
      loadChatHeaders(1, false);
    }
  }, [selectedModule]);

  return (
    <div>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-all duration-300 lg:hidden"
          onClick={toggleSidebar}
        ></div>
      )}
      <div
        className={`bg-neutral-950 text-white border-r border-slate-700 fixed lg:static top-0 left-0 z-50 w-auto h-full transition-all duration-300 flex flex-col
        ${isOpen ? 'h-full w-full sm:w-80 lg:w-64' : 'h-16 w-0'}`}
      >
        {isOpen && (
          <>
            <div className="w-full flex items-center justify-between p-4 py-5">
              <button onClick={toggleSidebar} className="cursor-pointer" title='Toggle Sidebar' aria-label='Toggle Sidebar'>
                <PanelRight size={20} />
              </button>
              <button 
                className="ml-auto cursor-pointer"
                title='Search Chat History'
                aria-label='Search Chat History'
                onClick={() => setIsSearchVisible(!isSearchVisible)}
              >
                <Search size={20} />
              </button>
              <Link to="/chat" className="ml-2 cursor-pointer" title='New Chat' aria-label='New Chat'>
                <SquarePlus size={20} />
              </Link>
            </div>

            {/* Search Input */}
            {isSearchVisible && (
              <div className="px-4 mb-4 relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search chat history..."
                  className="w-full bg-white/10 text-white px-3 py-2 rounded-md pr-8 focus:outline-none focus:ring-1 focus:ring-white/20"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            )}

            <section className="flex flex-col h-full">
              <h1 className="text-sm font-bold mb-4 px-4">Chat history</h1>
              <div className="space-y-4 overflow-y-auto flex-1 pr-2 pl-4" style={{ maxHeight: 'calc(100vh - 100px)' }}>
                {/* Today */}
                {chatGroups.today.length > 0 && (
                  <div>
                    <h2 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Today</h2>
                    <ul className="space-y-2">
                      {chatGroups.today.map((chat) => (
                        <li
                          key={chat.chat_id}
                          className="w-full p-2 hover:bg-white/20 rounded duration-300 cursor-pointer"
                          onClick={() => navigate(`/chat/${chat.chat_id}`)}
                        >
                          <span className="line-clamp-1 text-sm">{chat.header.replace(/"/g, '')}</span>
                          <small className="text-xs text-gray-400">
                            {formatTimestamp(chat.timestamp, true)}
                          </small>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Yesterday */}
                {chatGroups.yesterday.length > 0 && (
                  <div>
                    <h2 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Yesterday</h2>
                    <ul className="space-y-2">
                      {chatGroups.yesterday.map((chat) => (
                        <li
                          key={chat.chat_id}
                          className="w-full p-2 hover:bg-white/20 rounded duration-300 cursor-pointer"
                          onClick={() => navigate(`/chat/${chat.chat_id}`)}
                        >
                          <span className="line-clamp-1 text-sm">{chat.header.replace(/"/g, '')}</span>
                          <small className="text-xs text-gray-400">
                            {formatTimestamp(chat.timestamp, false, true)}
                          </small>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Last 7 Days */}
                {chatGroups.last7days.length > 0 && (
                  <div>
                    <h2 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Last 7 Days</h2>
                    <ul className="space-y-2">
                      {chatGroups.last7days.map((chat) => (
                        <li
                          key={chat.chat_id}
                          className="w-full p-2 hover:bg-white/20 rounded duration-300 cursor-pointer"
                          onClick={() => navigate(`/chat/${chat.chat_id}`)}
                        >
                          <span className="line-clamp-1 text-sm">{chat.header.replace(/"/g, '')}</span>
                          <small className="text-xs text-gray-400">
                            {formatTimestamp(chat.timestamp)}
                          </small>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Older */}
                {chatGroups.older.length > 0 && (
                  <div>
                    <h2 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wide">Older</h2>
                    <ul className="space-y-2">
                      {chatGroups.older.map((chat) => (
                        <li
                          key={chat.chat_id}
                          className="w-full p-2 hover:bg-white/20 rounded duration-300 cursor-pointer"
                          onClick={() => navigate(`/chat/${chat.chat_id}`)}
                        >
                          <span className="line-clamp-1 text-sm">{chat.header.replace(/"/g, '')}</span>
                          <small className="text-xs text-gray-400">
                            {formatTimestamp(chat.timestamp)}
                          </small>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* No results message for search */}
                {debouncedQuery && filteredChatHeaders().length === 0 && !isLoading && (
                  <div className="text-center text-gray-400 text-sm py-4">
                    No chats found matching "{debouncedQuery}"
                  </div>
                )}

                {/* No chats message */}
                {chatHeaders.length === 0 && !isLoading && !debouncedQuery && (
                  <div className="text-center text-gray-400 text-sm py-4">
                    No chat history yet
                  </div>
                )}

                {/* Load more button */}
                {hasMore && chatHeaders.length > 0 && (
                  <div className="">
                    <button
                      onClick={loadMoreChatHeaders}
                      disabled={isLoading}
                      className="w-full p-2 text-sm text-gray-400 hover:text-white hover:bg-white/10 rounded border border-gray-600/50 hover:border-gray-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? 'Loading...' : 'Load more chats'}
                    </button>
                  </div>
                )}

                {/* Loading indicator for initial load */}
                {isLoading && chatHeaders.length === 0 && (
                  <div className="text-center text-gray-400 text-sm py-4">
                    Loading chat history...
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
